/**
 * 投后风控（p8）与风险预警中心（p10）交互 — 从 legacy-app.js 拆出。
 */
import { escapeHtml } from '../util/html.js';

/** @param {import('./portal-ui-deps.js').PortalUiDeps} deps */
export function initPostRiskUi(deps) {
  const {
    requestRuntime,
    showRuntimeError,
    setRuntimeProgress,
    getCurrentProjectId,
    getCurrentProjectInput,
    getLatestContractReview,
    getLatestDdQuestions,
  } = deps;

  let latestPostRisk = null;

  // ---- 投后预警闭环：由 Runtime 按项目+风险键持久化并审计 ----
  const postRiskClosureStore = {};
  const CLOSURE_STATUS_LABEL = {
    open: '待处置',
    watching: '观察中',
    mitigating: '处置中',
    resolved: '已关闭'
  };

  function closureProjectKey(result) {
    return result?.basicInfo?.projectId || getCurrentProjectId() || 'draft';
  }

  /** 规则风险用 ruleId 更稳；AI 风险回退 id */
  function closureRiskKey(item) {
    if (!item) return '';
    if (item.ruleId && item.ruleId !== 'AI') return `rule:${item.ruleId}`;
    return `id:${item.id || item.title || 'unknown'}`;
  }

  function getClosureRecord(projectKey, riskKey) {
    const projectBucket = postRiskClosureStore[projectKey];
    if (!projectBucket || typeof projectBucket !== 'object') return null;
    return projectBucket[riskKey] || null;
  }

  function cacheClosureRecord(record) {
    if (!postRiskClosureStore[record.projectId]) {
      postRiskClosureStore[record.projectId] = {};
    }
    postRiskClosureStore[record.projectId][record.riskKey] = record;
  }

  async function loadPostRiskClosures(projectId) {
    if (!projectId || projectId === 'draft') return;
    const result = await requestRuntime(
      `/api/business/projects/${encodeURIComponent(projectId)}/risk-closures`
    );
    postRiskClosureStore[projectId] = {};
    (result.closures || []).forEach(cacheClosureRecord);
  }

  async function upsertClosureRecord(projectId, riskKey, patch) {
    const result = await requestRuntime(
      `/api/business/projects/${encodeURIComponent(projectId)}/risk-closures`,
      {
        method: 'PUT',
        body: JSON.stringify({ riskKey, ...patch })
      }
    );
    cacheClosureRecord(result.closure);
    return result.closure;
  }

  function formatClosureTime(iso) {
    if (!iso) return '--';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return String(iso);
    return date.toLocaleString('zh-CN', { hour12: false });
  }

  /** 将服务端闭环叠加到 analyze 结果上，不改写引擎原始字段 */
  function applyClosureOverlays(risks, projectKey) {
    return (Array.isArray(risks) ? risks : []).map((item) => {
      const record = getClosureRecord(projectKey, closureRiskKey(item));
      if (!record) {
        return {
          ...item,
          closureStatus: item.status,
          closureOwner: item.ownerRole || '',
          acknowledgedAt: null,
          closureUpdatedAt: null
        };
      }
      return {
        ...item,
        // 展示用闭环状态（含 resolved）；引擎原始 status 仍可在详情「引擎状态」看到
        closureStatus: record.status || item.status,
        closureOwner: record.owner != null && record.owner !== ''
          ? record.owner
          : (item.ownerRole || ''),
        closureNote: record.note || '',
        acknowledgedAt: record.acknowledgedAt || null,
        closureUpdatedAt: record.updatedAt || null
      };
    });
  }

  function postRiskLevelLabel(level) {
    return ({ red: '红色', orange: '橙色', yellow: '黄色', blue: '蓝色' })[level] || level;
  }

  function renderPostRiskResult(result) {
    latestPostRisk = result;
    const stats = result.riskMonitoring?.statistics || {};
    document.getElementById('post-risk-title').textContent =
      `${result.basicInfo?.companyName || '被投企业'} · 投后风控`;
    document.getElementById('post-risk-overall').textContent =
      result.basicInfo?.overallRiskLabel || '已分析';
    document.getElementById('post-risk-stat-red').textContent = stats.red || 0;
    document.getElementById('post-risk-stat-orange').textContent = stats.orange || 0;
    document.getElementById('post-risk-stat-yellow').textContent = stats.yellow || 0;
    document.getElementById('post-risk-stat-blue').textContent = stats.blue || 0;
    const indicators = Array.isArray(result.currentIndicators) ? result.currentIndicators : [];
    document.getElementById('post-risk-indicators').innerHTML = indicators.map((item) => `
    <div style="background:#FCFAF5;border:1px solid #E0DBD2;border-radius:4px;padding:8px;">
      <div style="font-size:9px;color:#8A95A5;">${escapeHtml(item.label)}</div>
      <div style="font-size:12px;font-weight:600;color:#0B1426;">${escapeHtml(item.value)}</div>
      <div style="font-size:9px;color:#6B7280;">${escapeHtml(item.status)} · ${escapeHtml(item.trendHint || '')}</div>
    </div>
  `).join('') || '<div style="font-size:11px;color:#6B7280;">暂无指标</div>';
    const projectKey = closureProjectKey(result);
    const risks = applyClosureOverlays(
      Array.isArray(result.riskMonitoring?.activeRisks) ? result.riskMonitoring.activeRisks : [],
      projectKey
    );
    document.getElementById('post-risk-list').innerHTML = risks.length
      ? risks.map((item) => {
          const closureStatus = item.closureStatus || item.status;
          const owner = item.closureOwner || item.ownerRole || '';
          return `
        <div class="contract-diff-item ${item.level === 'red' ? 'high' : item.level === 'orange' || item.level === 'yellow' ? 'medium' : 'low'}">
          <div class="contract-diff-top">
            <span class="contract-risk-badge ${item.level === 'red' ? 'high' : item.level === 'blue' ? 'low' : 'medium'}">${postRiskLevelLabel(item.level)}</span>
            <span class="contract-diff-title">${escapeHtml(item.title)}</span>
          </div>
          <div class="contract-diff-detail">${escapeHtml(item.description)}</div>
          <div class="contract-diff-quote">${escapeHtml(item.evidence || '')}</div>
          <div class="contract-diff-detail" style="margin-top:4px;">建议：${escapeHtml(item.suggestion || '')} · SLA ${escapeHtml(item.responseSla || '')}</div>
          <div class="contract-diff-detail" style="margin-top:4px;color:#6B7280;">闭环：${escapeHtml(CLOSURE_STATUS_LABEL[closureStatus] || closureStatus || '--')}${owner ? ' · ' + escapeHtml(owner) : ''}${item.acknowledgedAt ? ' · 已知悉' : ''}</div>
        </div>`;
        }).join('')
      : '<div style="font-size:11px;color:#6B7280;">未识别到活跃风险</div>';
    document.getElementById('post-risk-report').textContent =
      result.regularReport?.summary || '暂无报告摘要';
    const actions = [
      ...(result.regularReport?.focusActions || []),
      ...(result.dispositionSuggestions || []).map((item) => item.suggestion)
    ].filter(Boolean);
    document.getElementById('post-risk-actions').innerHTML = actions.length
      ? actions.slice(0, 8).map((item) => `<li>${escapeHtml(item)}</li>`).join('')
      : '<li>暂无</li>';
    document.getElementById('post-risk-disclaimer').textContent =
      result.disclaimer || '';
    document.getElementById('stage-result-p8').innerHTML = `
    <strong>投后风控分析</strong>
    · ${escapeHtml(result.basicInfo?.overallRiskLabel || '')}
    · 活跃风险 ${stats.total || 0}
    · taskId <code style="font-size:10px;">${escapeHtml(result.taskId || '')}</code>`;
    renderAlertCenter(result);
  }

  document.getElementById('post-risk-from-project').addEventListener('click', function() {
    const currentProjectInput = getCurrentProjectInput();
    if (!getCurrentProjectId() && !currentProjectInput?.companyName) {
      showRuntimeError('请先创建或打开项目。');
      return;
    }
    document.getElementById('post-risk-company').value =
      currentProjectInput.companyName || '';
    document.getElementById('post-risk-project').value =
      currentProjectInput.companyName
        ? `${currentProjectInput.companyName}${currentProjectInput.financingRound ? ' ' + currentProjectInput.financingRound : ''}`
        : getCurrentProjectId();
    const latestContractReview = getLatestContractReview();
    if (latestContractReview?.reviewConclusion) {
      document.getElementById('post-risk-terms').value =
        latestContractReview.reviewConclusion.slice(0, 1500);
    }
    const latestDdQuestions = getLatestDdQuestions();
    if (latestDdQuestions?.ddOverview?.highRiskAreas?.length) {
      const update = document.getElementById('post-risk-update');
      update.value = [
        update.value,
        '投前风险观察点：' + latestDdQuestions.ddOverview.highRiskAreas.join('；')
      ].filter(Boolean).join('\n');
    }
  });

  function buildPostRiskAnalyzeBody() {
    const completion = document.getElementById('post-risk-completion').value;
    const runway = document.getElementById('post-risk-runway').value;
    const revenueYoy = document.getElementById('post-risk-revenue-yoy').value;
    const margin = document.getElementById('post-risk-margin').value;
    const latestDdQuestions = getLatestDdQuestions();
    return {
      projectId: getCurrentProjectId() || undefined,
      companyName: document.getElementById('post-risk-company').value.trim(),
      projectName: document.getElementById('post-risk-project').value.trim(),
      performanceTarget: document.getElementById('post-risk-target').value.trim(),
      keyTermsSummary: document.getElementById('post-risk-terms').value.trim(),
      operatingUpdate: document.getElementById('post-risk-update').value.trim(),
      performanceCompletionPct: completion === '' ? undefined : Number(completion),
      cashRunwayMonths: runway === '' ? undefined : Number(runway),
      revenueYoyPct: revenueYoy === '' ? undefined : Number(revenueYoy),
      grossMarginPct: margin === '' ? undefined : Number(margin),
      preRiskWatchpoints: latestDdQuestions?.ddOverview?.highRiskAreas || undefined,
      reportPeriod: 'quarterly'
    };
  }

  document.getElementById('post-risk-analyze-btn').addEventListener('click', async function() {
    const originalText = this.textContent;
    this.disabled = true;
    this.textContent = '分析中...';
    setRuntimeProgress(30);
    try {
      const result = await requestRuntime('/api/business/post-risk/analyze', {
        method: 'POST',
        body: JSON.stringify(buildPostRiskAnalyzeBody())
      });
      await loadPostRiskClosures(closureProjectKey(result));
      renderPostRiskResult(result);
      setRuntimeProgress(100);
    } catch (error) {
      document.getElementById('stage-result-p8').textContent = `投后风控失败：${error.message}`;
      showRuntimeError(error.message);
      setRuntimeProgress(0);
    } finally {
      this.disabled = false;
      this.textContent = originalText;
    }
  });

  let latestAlertRisks = [];
  let selectedAlertId = null;

  const ALERT_LEVEL_RANK = { red: 0, orange: 1, yellow: 2, blue: 3 };
  const ALERT_DIMENSION_LABEL = {
    operation: '经营风险',
    finance: '财务风险',
    legal_compliance: '法律合规',
    market_industry: '市场行业',
    team_governance: '团队治理',
    contract_commitment: '合同承诺'
  };
  const ALERT_STATUS_LABEL = {
    open: '待处置',
    watching: '观察中',
    mitigating: '处置中',
    resolved: '已关闭'
  };

  function alertLevelCss(level) {
    if (level === 'red') return 'high';
    if (level === 'orange' || level === 'yellow') return 'medium';
    return 'low';
  }

  function sortActiveRisks(risks) {
    return [...risks].sort((a, b) => {
      const rankA = ALERT_LEVEL_RANK[a.level] ?? 9;
      const rankB = ALERT_LEVEL_RANK[b.level] ?? 9;
      if (rankA !== rankB) return rankA - rankB;
      // 未关闭优先，已关闭靠后
      const closedA = (a.closureStatus || a.status) === 'resolved' ? 1 : 0;
      const closedB = (b.closureStatus || b.status) === 'resolved' ? 1 : 0;
      if (closedA !== closedB) return closedA - closedB;
      return String(a.title || '').localeCompare(String(b.title || ''), 'zh');
    });
  }

  function renderAlertDetail(item, result) {
    const empty = document.getElementById('alert-detail-empty');
    const detail = document.getElementById('alert-detail');
    if (!item) {
      empty.style.display = 'block';
      detail.style.display = 'none';
      return;
    }
    empty.style.display = 'none';
    detail.style.display = 'block';
    document.getElementById('alert-detail-level').textContent = postRiskLevelLabel(item.level);
    document.getElementById('alert-detail-level').style.color =
      item.level === 'red' ? '#B91C1C' : item.level === 'orange' ? '#C2410C' : '#0B1426';
    document.getElementById('alert-detail-title').textContent = item.title || '--';
    document.getElementById('alert-detail-sla').textContent = item.responseSla || '--';
    document.getElementById('alert-detail-project').textContent =
      result?.basicInfo?.projectName || result?.basicInfo?.companyName || '--';
    document.getElementById('alert-detail-dimension').textContent =
      ALERT_DIMENSION_LABEL[item.dimension] || item.dimension || '--';
    // 引擎原始状态（analyze 产出）；闭环状态在下方面板编辑
    document.getElementById('alert-detail-status').textContent =
      ALERT_STATUS_LABEL[item.status] || item.status || '--';
    document.getElementById('alert-detail-evidence').textContent =
      [item.evidence, item.description, item.impact].filter(Boolean).join(' / ') || '--';
    document.getElementById('alert-detail-suggestion').textContent =
      item.suggestion || '暂无处置建议，请人工研判。';
    document.getElementById('alert-detail-disclaimer').textContent =
      result?.disclaimer || '';
    const statusSelect = document.getElementById('alert-closure-status');
    const ownerInput = document.getElementById('alert-closure-owner');
    const noteInput = document.getElementById('alert-closure-note');
    const slaEl = document.getElementById('alert-closure-sla');
    const ackEl = document.getElementById('alert-closure-ack');
    const updatedEl = document.getElementById('alert-closure-updated');
    if (statusSelect) {
      const closureStatus = item.closureStatus || item.status || 'open';
      statusSelect.value = CLOSURE_STATUS_LABEL[closureStatus] ? closureStatus : 'open';
    }
    if (ownerInput) {
      ownerInput.value = item.closureOwner || item.ownerRole || '';
    }
    if (noteInput) {
      noteInput.value = item.closureNote || '';
    }
    if (slaEl) slaEl.textContent = item.responseSla || '--';
    if (ackEl) {
      ackEl.textContent = item.acknowledgedAt
        ? formatClosureTime(item.acknowledgedAt)
        : '未确认';
    }
    if (updatedEl) {
      updatedEl.textContent = item.closureUpdatedAt
        ? formatClosureTime(item.closureUpdatedAt)
        : '--';
    }
    const indicators = Array.isArray(result?.currentIndicators) ? result.currentIndicators : [];
    const focusIndicators = indicators.filter((row) =>
      row.status === 'alert' || row.status === 'watch'
    );
    const kpiRows = (focusIndicators.length ? focusIndicators : indicators).slice(0, 6);
    document.getElementById('alert-detail-kpis').innerHTML = kpiRows.length
      ? kpiRows.map((row) => `
        <div style="background:#FCFAF5;border:1px solid #E0DBD2;border-radius:4px;padding:8px 10px;min-width:120px;">
          <div style="font-size:9px;color:#8A95A5;">${escapeHtml(row.label)}</div>
          <div style="font-size:12px;font-weight:600;color:#0B1426;">${escapeHtml(row.value)}</div>
          <div style="font-size:9px;color:#6B7280;">${escapeHtml(row.status)} · ${escapeHtml(row.trendHint || '')}</div>
        </div>
      `).join('')
      : '<div style="font-size:11px;color:#6B7280;">暂无关联指标</div>';
  }

  function renderAlertCenter(result) {
    latestPostRisk = result;
    const stats = result.riskMonitoring?.statistics || {};
    const projectKey = closureProjectKey(result);
    const risks = sortActiveRisks(
      applyClosureOverlays(
        Array.isArray(result.riskMonitoring?.activeRisks) ? result.riskMonitoring.activeRisks : [],
        projectKey
      )
    );
    latestAlertRisks = risks;
    if (selectedAlertId && !risks.some((item) => item.id === selectedAlertId)) {
      selectedAlertId = null;
    }
    if (!selectedAlertId && risks.length) {
      selectedAlertId = risks[0].id;
    }
    const openCount = risks.filter((item) => (item.closureStatus || item.status) !== 'resolved').length;
    document.getElementById('alert-list-meta').textContent =
      `${risks.length} 条 · 未关闭 ${openCount} · 红 ${stats.red || 0} / 橙 ${stats.orange || 0}`;
    document.getElementById('alert-list').innerHTML = risks.length
      ? risks.map((item) => {
          const selected = item.id === selectedAlertId;
          const closureStatus = item.closureStatus || item.status;
          const owner = item.closureOwner || item.ownerRole || '';
          return `
          <button type="button" data-alert-id="${escapeHtml(item.id)}"
            style="text-align:left;width:100%;cursor:pointer;border:1px solid ${selected ? '#C5A55A' : '#E0DBD2'};border-radius:5px;padding:10px;background:${selected ? '#FCFAF5' : '#fff'};opacity:${closureStatus === 'resolved' ? '0.72' : '1'};">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
              <span class="contract-risk-badge ${alertLevelCss(item.level)}">${postRiskLevelLabel(item.level)}</span>
              <span style="font-size:12px;font-weight:600;color:#0B1426;">${escapeHtml(item.title)}</span>
            </div>
            <div style="font-size:11px;color:#6B7280;line-height:1.5;">${escapeHtml(item.description || '')}</div>
            <div style="font-size:10px;color:#9CA3AF;margin-top:4px;">SLA ${escapeHtml(item.responseSla || '--')} · ${escapeHtml(ALERT_DIMENSION_LABEL[item.dimension] || item.dimension || '')}</div>
            <div style="font-size:10px;color:#6B7280;margin-top:4px;">闭环 ${escapeHtml(CLOSURE_STATUS_LABEL[closureStatus] || closureStatus || '--')}${owner ? ' · ' + escapeHtml(owner) : ''}${item.acknowledgedAt ? ' · 已知悉' : ''}</div>
          </button>`;
        }).join('')
      : '<div style="font-size:11px;color:#6B7280;">未识别到活跃风险预警</div>';
    const selected = risks.find((item) => item.id === selectedAlertId) || null;
    renderAlertDetail(selected, result);
    document.getElementById('stage-result-p10').innerHTML = `
    <strong>风险预警中心</strong>
    · ${escapeHtml(result.basicInfo?.overallRiskLabel || '')}
    · 活跃 ${stats.total || 0}（红 ${stats.red || 0} / 橙 ${stats.orange || 0}）· 未关闭 ${openCount}
    · taskId <code style="font-size:10px;">${escapeHtml(result.taskId || '')}</code>`;
  }

  /** 保存当前选中预警的闭环字段到 Runtime，并刷新 P8/P10 展示 */
  async function saveSelectedAlertClosure(extraPatch) {
    if (!latestPostRisk || !selectedAlertId) {
      showRuntimeError('请先刷新并选择一条预警。');
      return;
    }
    const item = latestAlertRisks.find((row) => row.id === selectedAlertId)
      || (latestPostRisk.riskMonitoring?.activeRisks || []).find((row) => row.id === selectedAlertId);
    if (!item) {
      showRuntimeError('未找到当前预警，请重新刷新。');
      return;
    }
    const statusSelect = document.getElementById('alert-closure-status');
    const ownerInput = document.getElementById('alert-closure-owner');
    const noteInput = document.getElementById('alert-closure-note');
    const status = statusSelect?.value || item.closureStatus || item.status || 'open';
    const owner = (ownerInput?.value || '').trim();
    const note = (noteInput?.value || '').trim();
    const projectKey = closureProjectKey(latestPostRisk);
    if (projectKey === 'draft') {
      showRuntimeError('请先创建或打开项目，再保存风险闭环。');
      return;
    }
    const riskKey = closureRiskKey(item);
    await upsertClosureRecord(projectKey, riskKey, {
      status,
      owner: owner || item.ownerRole || '',
      note: note || undefined,
      acknowledgedAt: item.acknowledgedAt || null,
      ...(extraPatch || {})
    });
    // renderPostRiskResult 会联动刷新 P10 预警中心
    renderPostRiskResult(latestPostRisk);
  }

  document.getElementById('alert-list').addEventListener('click', function(event) {
    const button = event.target.closest('[data-alert-id]');
    if (!button) return;
    selectedAlertId = button.getAttribute('data-alert-id');
    if (latestPostRisk) {
      renderAlertCenter(latestPostRisk);
    }
  });

  document.getElementById('alert-closure-save-btn').addEventListener('click', async function() {
    try {
      await saveSelectedAlertClosure();
    } catch (error) {
      showRuntimeError(`闭环保存失败：${error.message}`);
    }
  });

  document.getElementById('alert-closure-ack-btn').addEventListener('click', async function() {
    try {
      await saveSelectedAlertClosure({ acknowledgedAt: new Date().toISOString() });
    } catch (error) {
      showRuntimeError(`知悉确认失败：${error.message}`);
    }
  });

  document.getElementById('alert-from-p8').addEventListener('click', function() {
    document.getElementById('post-risk-from-project').click();
    const company = document.getElementById('post-risk-company').value.trim();
    const project = document.getElementById('post-risk-project').value.trim();
    if (!company && !project && !document.getElementById('post-risk-update').value.trim()) {
      showRuntimeError('请先在投后风控页填写企业/经营更新，或创建项目后再带入。');
      return;
    }
    document.getElementById('stage-result-p10').textContent =
      `已同步投后页输入：${company || project || '经营更新'}。点击「刷新预警」拉取活跃风险。`;
  });

  document.getElementById('alert-refresh-btn').addEventListener('click', async function() {
    const body = buildPostRiskAnalyzeBody();
    if (!body.companyName && !body.projectName && !body.operatingUpdate && !body.keyTermsSummary) {
      showRuntimeError('请先填写投后风控页输入，或点击「带入投后页输入」。');
      return;
    }
    const originalText = this.textContent;
    this.disabled = true;
    this.textContent = '刷新中...';
    setRuntimeProgress(30);
    try {
      const result = await requestRuntime('/api/business/post-risk/analyze', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      await loadPostRiskClosures(closureProjectKey(result));
      renderPostRiskResult(result);
      setRuntimeProgress(100);
    } catch (error) {
      document.getElementById('stage-result-p10').textContent = `预警刷新失败：${error.message}`;
      showRuntimeError(error.message);
      setRuntimeProgress(0);
    } finally {
      this.disabled = false;
      this.textContent = originalText;
    }
  });

  return { loadPostRiskClosures };
}
